import {
  addToRecord,
  arrayWithoutSubset,
  objectWithoutKeys,
  updateRecord,
  updateRecordWhere,
} from '@ms/yammer-data-support';

import type { NestedThreadLoadFulfilledAction, NestedThreadLoadRejectedAction } from '../../domains/nestedThread/types';
import type {
  PublisherContentChangedAction,
  PublisherContentTypeChangedAction,
  PublisherIdChangedAction,
  PublisherPollOptionChangedAction,
  PublisherPostTypeChangedAction,
  PublisherPraisedUsersAddedAction,
  PublisherPraisedUsersChangedAction,
  PublisherPraisedUsersRemovedAction,
  PublisherTitleChangedAction,
  PublisherTranslatableContentChangedAction,
} from '../../domains/publishers/actions.types';
import type { Message, Publishers } from '../types';

import type { PublishersAction } from './reducer.types';
import type { Publisher } from './types';
import {
  isEditPublisher,
  isGroupQuestionPublisher,
  isNetworkQuestionNewQuestionPublisher,
  isPollPublisher,
  isPraisePublisher,
  isUserThreadStarterPublisher,
} from './types';

const emptyOption = '';
const maxPollOptions = 100;
const maxPollOptionLength = 1000;
const isPollOptionEmpty = (pollOption: string) => pollOption.trim() === '';
const shouldAddEmptyOption = (pollOptions: string[]) => {
  const canAddOption = pollOptions.length < maxPollOptions;
  const hasFilledAllOptions = pollOptions.every((pollOption: string) => !isPollOptionEmpty(pollOption));

  return canAddOption && hasFilledAllOptions;
};
const isValidPollContentLength = (pollOptionContent: string) => pollOptionContent.length <= maxPollOptionLength;
const changePollOption = (state: Publishers, payload: PublisherPollOptionChangedAction['payload']) => {
  const { id, pollOptionIndex, pollOptionContent } = payload;

  const previousPublisher = state[id];
  if (!previousPublisher || (!isPollPublisher(previousPublisher) && !isEditPublisher(previousPublisher))) {
    return state;
  }

  if (!previousPublisher.pollOptions) {
    return state;
  }

  let pollOptions = [...previousPublisher.pollOptions];
  if (isValidPollContentLength(pollOptionContent)) {
    pollOptions = Object.assign([...pollOptions], { [pollOptionIndex]: pollOptionContent });
  }

  if (shouldAddEmptyOption(pollOptions)) {
    pollOptions = [...pollOptions, emptyOption];
  }

  return {
    ...state,
    [id]: {
      ...previousPublisher,
      pollOptions,
    },
  };
};

const addPraisedUsersToPublisher = (state: Publishers, payload: PublisherPraisedUsersAddedAction['payload']) => {
  const { id, userIds } = payload;

  const previousPublisher = state[id];
  if (!previousPublisher || (!isPraisePublisher(previousPublisher) && !isEditPublisher(previousPublisher))) {
    return state;
  }

  const uniquePraisedUserIds = Array.from(new Set([...previousPublisher.praisedUserIds, ...userIds]));

  return {
    ...state,
    [id]: {
      ...previousPublisher,
      praisedUserIds: uniquePraisedUserIds,
    },
  };
};

const removePraisedUsersFromPublisher = (
  state: Publishers,
  { id, userIds }: PublisherPraisedUsersRemovedAction['payload']
) => {
  const previousPublisher = state[id];
  if (!previousPublisher || (!isPraisePublisher(previousPublisher) && !isEditPublisher(previousPublisher))) {
    return state;
  }

  const praisedUserIds = arrayWithoutSubset(previousPublisher.praisedUserIds, userIds);

  return {
    ...state,
    [id]: {
      ...previousPublisher,
      praisedUserIds,
    },
  };
};

const changePraisedUsersFromPublisher = (
  state: Publishers,
  { id, userIds }: PublisherPraisedUsersChangedAction['payload']
) => {
  const previousPublisher = state[id];
  if (!previousPublisher || (!isPraisePublisher(previousPublisher) && !isEditPublisher(previousPublisher))) {
    return state;
  }

  return {
    ...state,
    [id]: {
      ...previousPublisher,
      praisedUserIds: userIds,
    },
  };
};

const updatePublisherPostType = (state: Publishers, payload: PublisherPostTypeChangedAction['payload']) => {
  const { id, postType } = payload;

  const previousPublisher = state[id];
  if (!previousPublisher) {
    return state;
  }

  const shouldUseUpdatePostType = previousPublisher.postType === postType;
  const updatePublisherPostType = isUserThreadStarterPublisher(previousPublisher) ? 'UserNewUpdate' : 'GroupNewUpdate';
  const newPostType = shouldUseUpdatePostType ? updatePublisherPostType : postType;

  const fieldToUpdate: Pick<Publisher, 'postType'> = { postType: newPostType };
  const fieldToUpdateAsPartial = fieldToUpdate as Partial<Publisher>;

  return updateRecord(state, id, fieldToUpdateAsPartial);
};

const updatePublisherContentType = (state: Publishers, payload: PublisherContentTypeChangedAction['payload']) => {
  const { id, contentType } = payload;

  const previousPublisher = state[id];
  if (!previousPublisher) {
    return state;
  }

  return updateRecord(state, id, { contentType });
};

const changePublisherId = (state: Publishers, payload: PublisherIdChangedAction['payload']) => {
  const { currentPublisherId, newPublisherId } = payload;

  const previousPublisher: Publisher = state[currentPublisherId];
  if (!previousPublisher) {
    return state;
  }

  const publisherEntity = { ...previousPublisher, id: newPublisherId };
  const newState = objectWithoutKeys(state, [currentPublisherId]);

  return addToRecord(newState, newPublisherId, publisherEntity);
};

type UpdateContent = (state: Publishers, payload: PublisherContentChangedAction['payload']) => Publishers;
const updateContent: UpdateContent = (state, { id, rawContentState }): Publishers => {
  if (!state[id]) {
    return state;
  }

  const publisher = state[id];
  const currentRawContentState = publisher.rawContentState;

  if (
    (isNetworkQuestionNewQuestionPublisher(publisher) || isGroupQuestionPublisher(publisher)) &&
    currentRawContentState !== rawContentState &&
    currentRawContentState != null
  ) {
    return updateRecord(state, id, {
      rawContentState,
      hasCollapsedRelatedQuestionsModule: true,
      isBlockedByModeration: false,
    });
  }

  return updateRecord(state, id, {
    rawContentState,
    isBlockedByModeration: false,
  });
};

type UpdateTitle = (state: Publishers, payload: PublisherTitleChangedAction['payload']) => Publishers;
const updateTitle: UpdateTitle = (state, { title, id }) => {
  if (!state[id]) {
    return state;
  }

  return updateRecord(state, id, {
    title,
    hasCollapsedRelatedQuestionsModule: false,
    isBlockedByModeration: false,
  });
};

type UpdateTranslatableContent = (
  state: Publishers,
  payload: PublisherTranslatableContentChangedAction['payload']
) => Publishers;
const updateTranslatableContent: UpdateTranslatableContent = (
  state,
  { id, title, attachments, headerImage, notifiedUserIds, hasRemovedAttachmentsNotDeletedFromSharePoint }
) => {
  if (!state[id]) {
    return state;
  }

  return updateRecord(state, id, {
    title,
    attachments,
    headerImage,
    notifiedUserIds,
    hasRemovedAttachmentsNotDeletedFromSharePoint,
  });
};

const updateFulfilledSharedNestedThread = (
  state: Publishers,
  payload: NestedThreadLoadFulfilledAction['payload']
): Publishers => {
  const threadId = payload.threadId;
  const threadStarterId = payload.entities.threads[threadId]?.threadStarterId;
  const threadStarter = payload.entities.messages?.[threadStarterId];

  return updateSharedThreadByThreadStarter(state, threadStarter);
};

const removeFailedSharedNestedThread = (
  state: Publishers,
  payload: NestedThreadLoadRejectedAction['payload']
): Publishers => removeSharedThreadByThreadId(state, payload.threadId);

const removeSharedThreadByThreadId = (state: Publishers, threadId: string): Publishers =>
  updateRecordWhere(state, (publisher) => publisher.sharedThreadId === threadId, { sharedThreadId: undefined });

const updateSharedThreadByThreadStarter = (state: Publishers, threadStarter: Message | undefined): Publishers => {
  if (threadStarter && !threadStarter.viewerCanShare) {
    return removeSharedThreadByThreadId(state, threadStarter.threadId);
  }

  return state;
};

const toggleRelatedQuestionsModule = (state: Publishers, publisherId: Publisher['id']) => {
  const publisher = state[publisherId];

  if (isNetworkQuestionNewQuestionPublisher(publisher) || isGroupQuestionPublisher(publisher)) {
    return updateRecord(state, publisherId, {
      hasCollapsedRelatedQuestionsModule: !publisher.hasCollapsedRelatedQuestionsModule,
    });
  }

  return state;
};

type ContentActionsReducer = (state: Publishers, action: PublishersAction) => Publishers;
export const contentActionsReducer: ContentActionsReducer = (state, action) => {
  switch (action.type) {
    case 'PUBLISHER.POLL_OPTION_CHANGED':
      return changePollOption(state, action.payload);
    case 'PUBLISHER.PRAISED_USERS_ADDED':
      return addPraisedUsersToPublisher(state, action.payload);
    case 'PUBLISHER.PRAISED_USERS_REMOVED':
      return removePraisedUsersFromPublisher(state, action.payload);
    case 'PUBLISHER.PRAISED_USERS_CHANGED':
      return changePraisedUsersFromPublisher(state, action.payload);
    case 'PUBLISHER.POST_TYPE_CHANGED':
      return updatePublisherPostType(state, action.payload);
    case 'PUBLISHER.CONTENT_TYPE_CHANGED':
      return updatePublisherContentType(state, action.payload);
    case 'PUBLISHER.CONTENT_CHANGED':
      return updateContent(state, action.payload);
    case 'PUBLISHER.TITLE_CHANGED':
      return updateTitle(state, action.payload);
    case 'PUBLISHER.TRANSLATABLE_CONTENT_CHANGED':
      return updateTranslatableContent(state, action.payload);
    case 'PUBLISHER.SHARED_THREAD_ADDED':
      return updateRecord(state, action.payload.publisherId, { sharedThreadId: action.payload.sharedThreadId });
    case 'PUBLISHER.SHARED_THREAD_REMOVED':
      return updateRecord(state, action.payload.publisherId, { sharedThreadId: undefined });
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return updateFulfilledSharedNestedThread(state, action.payload);
    case 'NESTED_THREAD.LOAD_REJECTED':
      return removeFailedSharedNestedThread(state, action.payload);
    case 'PUBLISHER.RELATED_QUESTIONS_MODULE_TOGGLED':
      return toggleRelatedQuestionsModule(state, action.payload.publisherId);
    case 'PUBLISHER.SET_CAMPAIGN_REFERENCES':
      return updateRecord(state, action.payload.id, { campaignReferences: action.payload.campaignReferences });
    case 'PUBLISHER.ID_CHANGED':
      return changePublisherId(state, action.payload);
    default:
      return state;
  }
};
